import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AuthContext } from '../../App';
import { safeJson } from '../../utils/api';
import BrandLogo from '../BrandLogo';
import TradingViewWidget from './TradingViewWidget';
import OrderPanel from './OrderPanel';
import Balance from './Balance';
import TradeHistory from './TradeHistory';
import RecentTrades from './RecentTrades';
import WithdrawModal from './WithdrawModal';
import DepositModal from './DepositModal';
import DocumentsModal from './DocumentsModal';
import ChangePasswordModal from './ChangePasswordModal';
import AssetSelector from './AssetSelector';
import EconomicCalendar from './EconomicCalendar';
import FundamentalAnalysis from './FundamentalAnalysis';
import NotificationBell from './NotificationBell';
import ClientMessages from './ClientMessages';
import { 
  TrendingUp, 
  DollarSign, 
  ArrowUpCircle, 
  ArrowDownCircle,
  History,
  CreditCard,
  Settings,
  ChevronDown,
  LogOut,
  Wallet,
  Calendar,
  BarChart3,
  FileText,
  Lock,
  Smartphone,
  Menu,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TradingApp = () => {
  const authContext = useContext(AuthContext);
  const { user, logout } = authContext || {};
  const [selectedAsset, setSelectedAsset] = useState('EURUSD');
  const { tt } = useLanguage();
  const [currentPrice, setCurrentPrice] = useState(1.0850);
  const [balance, setBalance] = useState(0);
  const [activeTab, setActiveTab] = useState('trade');
  const [tradesTab, setTradesTab] = useState('open'); // New state for trades tabs
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  
  // НОВАЯ ЛОГИКА: Отдельные состояния для активных и закрытых сделок
  const [allTrades, setAllTrades] = useState([]); // Все сделки с backend
  const [activeTrades, setActiveTrades] = useState([]); // Только активные
  const [closedTrades, setClosedTrades] = useState([]); // Только закрытые
  const [totalProfit, setTotalProfit] = useState(0); // Общая прибыль из торгового счета
  
  const [language, setLanguage] = useState('ru');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notification, setNotification] = useState(null); // New state for notifications
  const [canInstallPWA, setCanInstallPWA] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOrderOpen, setMobileOrderOpen] = useState(false);
  const userMenuRef = useRef(null);
  
  // Проверяем возможность установки PWA
  useEffect(() => {
    const checkInstallable = () => {
      setCanInstallPWA(!!window.deferredPrompt);
    };
    
    checkInstallable();
    window.addEventListener('beforeinstallprompt', checkInstallable);
    const interval = setInterval(checkInstallable, 2000);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', checkInstallable);
      clearInterval(interval);
    };
  }, []);

  const handleInstallPWA = () => {
    if (window.installPWA) {
      window.installPWA();
      setShowUserMenu(false);
    } else {
      // Если beforeinstallprompt не сработал, показываем инструкцию
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert(tt('Для установки нажмите кнопку "Поделиться" в Safari, затем "На экран Домой"'));
      } else {
        alert(tt('Откройте меню браузера (⋮) и выберите "Установить приложение" или "Добавить на главный экран"'));
      }
      setShowUserMenu(false);
    }
  };
  
  // Test mode data when user is not authenticated
  const testUser = user || {
    name: 'Test User',
    platform_id: 'TP123456789'
  };
  const testBalance = balance || 1000;

  // Обновление цены актива из графика
  const handlePriceUpdate = useCallback((newPrice) => {
    setCurrentPrice(newPrice);
  }, []); // Пустой массив зависимостей, так как функция не зависит от внешних переменных

  // Обработчик изменения актива
  const handleAssetChange = (asset) => {
    setSelectedAsset(asset);
    // Сбрасываем цену при смене актива, она обновится из графика
    const basePrices = {
      'EURUSD': 1.0850,
      'GBPUSD': 1.2650,
      'USDJPY': 149.50,
      'USDCHF': 0.9120,
      'AUDUSD': 0.6720,
      'BTCUSD': 43250.00,
      'ETHUSD': 2650.00,
      'AAPL': 175.30,
      'GOOGL': 130.25,
      'TSLA': 245.80
    };
    setCurrentPrice(basePrices[asset] || 1.0000);
  };

  // Handle user logout
  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initial data fetch on component mount
  useEffect(() => {
    console.log('🚀 TradingApp: Component mounted, fetching initial data...');
    fetchTradingBalance();
    fetchTrades();
    
    // Set up MORE FREQUENT balance refresh - every 3 seconds for near real-time updates
    const balanceInterval = setInterval(() => {
      if (user) {
        fetchTradingBalance();
      }
    }, 3000); // ИЗМЕНЕНО: 3 секунды вместо 10
    
    return () => clearInterval(balanceInterval);
  }, []); // Run once on mount

  // Refetch balance when user context changes (login/logout) with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (user) {
        console.log('👤 TradingApp: User authenticated, fetching balance for user:', user.id);
        fetchTradingBalance();
      } else {
        console.log('🚫 TradingApp: No user authenticated, using demo balance');
      }
    }, 500); // 500ms debounce to prevent race conditions
    
    return () => clearTimeout(timeoutId);
  }, [user]); // Run when user changes

  // Fetch trades when asset changes and set up auto-refresh
  // При монтировании компонента - загрузить все данные
  useEffect(() => {
    console.log('🚀 TradingApp mounted - initial data load');
    fetchTradingBalance();
    fetchTrades();
  }, []);

  // Автообновление сделок каждые 3 секунды
  useEffect(() => {
    console.log('🔄 TradingApp: Setting up trades auto-refresh');
    
    const tradesInterval = setInterval(() => {
      fetchTrades();
    }, 3000);
    
    return () => {
      clearInterval(tradesInterval);
    };
  }, []);

  // Обновление баланса при изменении актива
  useEffect(() => {
    console.log('🔄 TradingApp: Asset changed to:', selectedAsset);
    fetchTradingBalance();
  }, [selectedAsset]);

  // Перезагрузка данных при переходе на страницу истории
  useEffect(() => {
    if (activeTab === 'history') {
      console.log('📖 Opening history page - refreshing trades data');
      fetchTrades();
    }
  }, [activeTab]);

  const fetchTradingBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Trading Platform: Fetching balance...', { token: token ? 'exists' : 'missing' });
      
      const response = await fetch(`${BACKEND_URL}/api/trading/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📊 Trading Platform: Balance API response:', { 
        status: response.status, 
        ok: response.ok 
      });
      
      if (response.ok) {
        const data = await safeJson(response);
        console.log('💰 Trading Platform: Balance data received:', data);
        
        // Add NaN protection when setting balance from API
        const apiBalance = parseFloat(data.balance);
        const safeBalance = isNaN(apiBalance) ? 0.00 : apiBalance;
        
        // Сохраняем total_profit из торгового счета
        const apiProfit = parseFloat(data.total_profit);
        const safeProfit = isNaN(apiProfit) ? 0.00 : apiProfit;
        
        setBalance(safeBalance);
        setTotalProfit(safeProfit);
        console.log('✅ Trading Platform: Balance set to:', safeBalance, 'Total Profit:', safeProfit);
        
        if (isNaN(apiBalance)) {
          console.warn('⚠️ Trading Platform: API returned non-numeric balance:', data.balance);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Trading Platform: Balance API error:', { 
          status: response.status, 
          error: errorText 
        });
      }
    } catch (error) {
      console.error('💥 Trading Platform: Balance fetch error:', error);
    }
  };

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/trading/trades`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await safeJson(response);
        
        // НОВАЯ ЛОГИКА: Разделяем сделки на активные и закрытые СРАЗУ после получения
        const active = [];
        const closed = [];
        
        data.forEach(trade => {
          // Проверяем статус строго
          if (trade.status === 'active') {
            active.push(trade);
          } else {
            // Все остальные статусы (won, lost, expired, cancelled) идут в закрытые
            closed.push(trade);
          }
        });
        
        console.log('📊 NEW LOGIC - Trades fetched and separated:', {
          total: data.length,
          active: active.length,
          closed: closed.length,
          activeIds: active.map(t => t.id).slice(0, 5),
          statuses: [...new Set(data.map(t => t.status))]
        });
        
        // Проверяем были ли закрыты активные сделки для автообновления баланса
        if (activeTrades.length > 0) {
          const prevActiveIds = new Set(activeTrades.map(t => t.id));
          const newCompletedIds = closed
            .filter(t => (t.status === 'won' || t.status === 'lost') && prevActiveIds.has(t.id))
            .map(t => t.id);
          
          if (newCompletedIds.length > 0) {
            console.log('🎯 Trades closed! Immediately updating balance...', newCompletedIds);
            setTimeout(() => fetchTradingBalance(), 100);
          }
        }
        
        // Обновляем ВСЕ три состояния одновременно
        setAllTrades(data);
        setActiveTrades(active);
        setClosedTrades(closed);
        
        console.log('✅ States updated:', {
          allTrades: data.length,
          activeTrades: active.length,
          closedTrades: closed.length
        });
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const handleTrade = async (tradeData) => {
    try {
      // Check if user has sufficient balance
      if (testBalance < tradeData.amount) {
        setNotification({
          type: 'error',
          message: 'Не достаточно баланса на торговом счету'
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/trading/place-trade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...tradeData,
          asset: selectedAsset,
          strike_price: currentPrice
        })
      });
      
      if (response.ok) {
        // Show success notification
        setNotification({
          type: 'success',
          message: 'Сделка успешно открыта'
        });
        setTimeout(() => setNotification(null), 3000);
        
        // Refresh data
        fetchTradingBalance();
        fetchTrades();
      } else {
        // Обработка ошибок от backend
        const errorData = await safeJson(response).catch(() => ({}));
        const errorMessage = errorData.detail || 'Ошибка при открытии сделки';
        
        setNotification({
          type: 'error',
          message: errorMessage
        });
        setTimeout(() => setNotification(null), 5000); // 5 секунд для важных сообщений
      }
    } catch (error) {
      console.error('Error placing trade:', error);
      setNotification({
        type: 'error',
        message: 'Ошибка при открытии сделки'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-[99999] px-6 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          {notification.message}
        </div>
      )}
      
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-gray-700 px-3 md:px-6 py-3 md:py-4 relative z-[99999]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 md:space-x-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-1"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            {/* Logo */}
            <div className="flex items-center">
              <BrandLogo size="md" />
            </div>
            
            {/* Asset Selector & Navigation - hidden on mobile, shown in mobile menu */}
            <div className="hidden md:flex items-center space-x-4 ml-32 relative z-[99999]">
              <AssetSelector
                selectedAsset={selectedAsset}
                onAssetChange={handleAssetChange}
              />
              <button
                onClick={() => setActiveTab('calendar')}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  activeTab === 'calendar' 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-white/10 text-gray-300 hover:bg-white/15'
                }`}
              >
                <Calendar className="w-4 h-4" />
                <span>{tt('Календарь')}</span>
              </button>
              <button
                onClick={() => setActiveTab('fundamental')}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  activeTab === 'fundamental' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-white/10 text-gray-300 hover:bg-white/15'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>{tt('Анализ')}</span>
              </button>
            </div>
          </div>

          {/* Right Side - User Info, Balance, User Menu */}
          <div className="flex items-center space-x-2 md:space-x-4 relative z-[100000]">
            {/* Balance */}
            <Balance 
              balance={testBalance} 
              profit={totalProfit} 
              onRefresh={fetchTradingBalance}
            />

            {/* Notifications - Only for CRM users, not clients */}
            {user?.role && user.role !== 'client' && (
              <NotificationBell userId={user?.id} />
            )}

            {/* User Menu Dropdown */}
            <div className="relative z-[99999]" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 md:space-x-3 bg-white/10 rounded-lg px-2 md:px-4 py-2 hover:bg-white/15 transition-colors"
              >
                <img 
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face&facepad=2&rounded-full"
                  alt="Profile"
                  className="w-8 h-8 rounded-full"
                />
                <div className="text-left hidden md:block">
                  <div className="text-white font-medium">{testUser?.name}</div>
                  <div className="text-gray-400 text-sm">
                    ID: {testUser?.platform_id || 'ID не найден'}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform hidden md:block ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-[999999]">
                  {/* Language Switcher */}
                  <div className="p-3 border-b border-gray-600">
                    <label className="block text-sm text-gray-300 mb-2">{tt('Язык / Language')}</label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ru">🇷🇺 Русский</option>
                      <option value="en">🇺🇸 English</option>
                    </select>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setShowDepositModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                    >
                      <CreditCard className="w-4 h-4 text-green-400" />
                      <span>{tt('Пополнить')}</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowWithdrawModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                    >
                      <Wallet className="w-4 h-4 text-blue-400" />
                      <span>{tt('Вывод средств')}</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowDocumentsModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                    >
                      <FileText className="w-4 h-4 text-purple-400" />
                      <span>{tt('Документы')}</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowChangePasswordModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                    >
                      <Lock className="w-4 h-4 text-yellow-400" />
                      <span>{tt('Смена пароля')}</span>
                    </button>

                    <button
                      onClick={handleInstallPWA}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                      data-testid="install-pwa-btn"
                    >
                      <Smartphone className="w-4 h-4 text-amber-400" />
                      <span>{tt('Скачать приложение')}</span>
                    </button>

                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors border-t border-gray-600 mt-2"
                    >
                      <LogOut className="w-4 h-4 text-red-400" />
                      <span>{tt('Выйти')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-900/95 backdrop-blur-sm border-b border-gray-700 px-3 py-3 z-[9999] relative">
          <div className="flex flex-col space-y-2">
            <AssetSelector
              selectedAsset={selectedAsset}
              onAssetChange={(asset) => { handleAssetChange(asset); setMobileMenuOpen(false); }}
            />
            <div className="flex space-x-2">
              <button
                onClick={() => { setActiveTab('calendar'); setMobileMenuOpen(false); }}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm ${
                  activeTab === 'calendar' ? 'bg-emerald-600 text-white' : 'bg-white/10 text-gray-300'
                }`}
              >
                <Calendar className="w-4 h-4" />
                <span>{tt('Календарь')}</span>
              </button>
              <button
                onClick={() => { setActiveTab('fundamental'); setMobileMenuOpen(false); }}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm ${
                  activeTab === 'fundamental' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>{tt('Анализ')}</span>
              </button>
              <button
                onClick={() => { setActiveTab('history'); setMobileMenuOpen(false); }}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm ${
                  activeTab === 'history' ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300'
                }`}
              >
                <History className="w-4 h-4" />
                <span>{tt('История')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row h-[calc(100vh-64px)] md:h-[calc(100vh-80px)]">
        {/* Sidebar - hidden on mobile */}
        <div className="hidden md:flex w-80 bg-black/30 backdrop-blur-sm border-r border-gray-700 p-4 flex-col">
          {/* Order Panel - moved to top */}
          {activeTab === 'trade' && (
            <div className="flex-1">
              <OrderPanel
                currentPrice={currentPrice}
                selectedAsset={selectedAsset}
                onTradeSubmit={handleTrade}
                balance={balance}
              />
            </div>
          )}

          {/* History Button - moved to sidebar */}
          <div className="mt-auto mb-4">
            <button
              onClick={() => setActiveTab('history')}
              className={`w-full px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                activeTab === 'history' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/15'
              }`}
            >
              <History className="w-5 h-5" />
              <span className="font-medium">{tt('История сделок')}</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 bg-gradient-to-br from-gray-900 to-black overflow-y-auto">
          {activeTab === 'history' ? (
            <div className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-lg md:text-2xl font-bold text-white">{tt('История сделок')}</h2>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 md:px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm md:text-base"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>{tt('К торговле')}</span>
                </button>
              </div>
              <TradeHistory trades={allTrades} key={allTrades.length} />
            </div>
          ) : activeTab === 'calendar' ? (
            <div className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-lg md:text-2xl font-bold text-white">{tt('Экономический календарь')}</h2>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 md:px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm md:text-base"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>{tt('К торговле')}</span>
                </button>
              </div>
              <EconomicCalendar />
            </div>
          ) : activeTab === 'fundamental' ? (
            <div className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-lg md:text-2xl font-bold text-white">{tt('Фундаментальный анализ')}</h2>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 md:px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm md:text-base"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>{tt('К торговле')}</span>
                </button>
              </div>
              <FundamentalAnalysis />
            </div>
          ) : (
            <div className="p-3 md:p-6 flex-1 flex flex-col">
              {/* Chart Section */}
              <div className="mb-3 md:mb-4 flex-shrink-0">
                <TradingViewWidget 
                  asset={selectedAsset}
                  onPriceUpdate={handlePriceUpdate}
                />
              </div>

              {/* Mobile Order Panel */}
              <div className="md:hidden mb-3">
                <button
                  onClick={() => setMobileOrderOpen(!mobileOrderOpen)}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2"
                  data-testid="mobile-trade-btn"
                >
                  <TrendingUp className="w-5 h-5" />
                  <span>{tt('Открыть сделку')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileOrderOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileOrderOpen && (
                  <div className="mt-2 bg-black/40 rounded-lg p-3 border border-gray-700">
                    <OrderPanel
                      currentPrice={currentPrice}
                      selectedAsset={selectedAsset}
                      onTradeSubmit={(data) => { handleTrade(data); setMobileOrderOpen(false); }}
                      balance={balance}
                    />
                  </div>
                )}
              </div>
              
              {/* Trades Section */}
              <div className="bg-gray-800 rounded-lg flex-shrink-0">
                {/* Tab Headers с счетчиками */}
                <div className="flex border-b border-gray-700">
                  <button
                    onClick={() => {
                      setTradesTab('open');
                    }}
                    className={`px-3 md:px-6 py-2 md:py-3 font-medium transition-colors text-sm md:text-base ${
                      tradesTab === 'open' 
                        ? 'text-white border-b-2 border-blue-500 bg-gray-700' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Открытые ({activeTrades.length})
                  </button>
                  <button
                    onClick={() => {
                      setTradesTab('closed');
                    }}
                    className={`px-3 md:px-6 py-2 md:py-3 font-medium transition-colors text-sm md:text-base ${
                      tradesTab === 'closed' 
                        ? 'text-white border-b-2 border-blue-500 bg-gray-700' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Закрытые ({closedTrades.length})
                  </button>
                </div>
                
                {/* Tab Content - ПРЯМАЯ передача без фильтрации */}
                <div className="p-4">
                  {tradesTab === 'open' ? (
                    <TradeHistory 
                      trades={activeTrades}
                      key={`open-${activeTrades.length}`}
                    />
                  ) : (
                    <TradeHistory 
                      trades={closedTrades}
                      key={`closed-${closedTrades.length}`}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <WithdrawModal
          balance={balance}
          onClose={() => setShowWithdrawModal(false)}
          onSuccess={() => {
            setShowWithdrawModal(false);
            fetchTradingBalance();
          }}
        />
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal
          onClose={() => setShowDepositModal(false)}
          onSuccess={() => {
            setShowDepositModal(false);
            fetchTradingBalance();
          }}
        />
      )}

      {/* Documents Modal */}
      {showDocumentsModal && (
        <DocumentsModal
          isOpen={showDocumentsModal}
          onClose={() => setShowDocumentsModal(false)}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          user={user}
        />
      )}

      {/* Client Messages - только для авторизованных клиентов */}
      {user && <ClientMessages />}
    </div>
  );
};

export default TradingApp;